import { initializeIcons, loadTheme, registerIcons } from "@fluentui/react";
import * as H from "history";
import * as moment from "moment";
import { trace } from "owa-trace";
import { StaffHubHttpError } from "sh-application";
import { BootstrapConfig, bootstrap } from "sh-application/bootstrap";
import { handleBlockingBootstrapError } from "sh-application/components/errorWatcher";
import iconsData from "sh-application/iconsData";
import initializeSatchelMiddleware from "sh-application/initializeSatchelMiddleware";
import initializeStrings from "sh-application/initializeStrings";
import { initializeTracer } from "sh-application/initializeTracer";
import DateTimeFormatter from "sh-application/utility/DateTimeFormatter";
import initializeErrorHandler from "sh-exception-handler/lib/utils/initializeErrorHandler";
import { isFeatureEnabled, setFeatureFlag } from "sh-feature-flags";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { UserEntity } from "sh-models";
import {
    AuthService,
    ClientVersionService,
    DataServices,
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService,
    NetworkService,
    SyncService,
    SmartScheduleGenerationPollingService,
} from "sh-services";
import { setHostPlatform, setUser } from "sh-stores";

import { ClientPlatformTypes, ClientTypes } from "../StaffHubConstants";
import { getClientTypeFromHostContext } from "./utility/utility";

import { HostClientType } from "./hostsContainers/HostClientType";
import { HostContext } from "./hostsContainers/HostContext";
import { IHostContainer } from "./hostsContainers/IHostContainer";
import { initializeThemes } from "./initializeTheme";

const styles = require("../../Theme.scss");

/**
 * Pre-initializes the application. Setup error handling and initializes Teams SDK.
 * @param appVersion The application version.
 * @param hostContainer The host container (e.g. TeamsHostContainer).
 */
export async function preinitApplication(appVersion: string, hostContainer: IHostContainer): Promise<void> {
    let initializationStage = "";
    try {
        initializationStage = "hostContainer.initialize";
        const initializationCallback = await hostContainer.initialize();

        // Get context from host (e.g. Teams)
        initializationStage = "hostContainer.getContext";
        const context = await hostContainer.getContext();

        initializationStage = "setUserInfoInState";
        setUserInfoInState(context.user.id, context.tenant.id);

        // Load the ecs config with value from service + base config
        initializationStage = "parseEcsConfigJson";
        const ecsConfig = JSON.parse(window.sa.ecsConfigJson);

        // Initialize the Instrumentation service
        initializationStage = "initializeInstrumentationService";
        await InstrumentationService.initialize(appVersion, context, ecsConfig);

        initializationStage = "initializeTheme";
        initializeThemes(context.app.theme);

        initializationStage = "setClientPlatformFromTeamsContext";
        setClientPlatformFromTeamsContext(context);

        initializationStage = "ECSConfigService.initializeECSConfig";
        ECSConfigService.initializeECSConfig(
            context.tenant.id,
            context.host.ringId,
            context.user.id,
            ecsConfig
        );

        // **** error handling ****
        // TODO(Performance): Remove verbose logging altogether to improve performance.
        initializationStage = "initializeLogging";
        initializeLogging();
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete initializing logging");

        // setting logged in user from teams context
        initializationStage = "DataServices.setUserLoggedIn";
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Setting user logged in within DataServices");
        trace.info("Setting user logged in within DataServices");
        const dataServicesLoginMarker = InstrumentationService.perfMarkerStart("setUserWithinDataServices");
        await DataServices.setUserLoggedIn(context.user.id, context.tenant.id, context.host.sessionId);
        InstrumentationService.perfMarkerEnd(dataServicesLoginMarker);
        trace.info("Complete setting user logged in within DataServices");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart,  "Complete setting user logged in within DataServices");

        // Give the dataservices a chance to prune unused data (but don't await this)
        setTimeout(async () => {
            trace.info("Pruning unusd data with DataServices");
            await DataServices.pruneData();
            trace.info("Complete pruning unused data with DataServices");
            // Start the logging session, if it hasn't already been started
            InstrumentationService.startLogSession();
        }, 5000);

        // **** Client versioning ****
        initializationStage = "ClientVersionService.init";
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Initializing client versioning");
        trace.info("Initializing client versioning");
        // Tell the user they need to refresh if a new version of the app is pushed
        ClientVersionService.init(appVersion);
        trace.info("Complete initialization of client versioning");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete initialization of client versioning");

        // **** Set Host Container ****
        // set the reportExpectedError function for the AuthService to reach the host app in case of errors
        initializationStage = "setReportExpectedErrorFunction";
        AuthService.setReportExpectedErrorFunction((error, reason) => hostContainer.reportExpectedError(error, reason));

        // **** Localized Strings ****
        // the final step in pre-initalizing the app is to download the localized strings.
        // this is done async and app initialization should not continue until all the strings
        // are downloaded.
        initializationStage = "initializeStrings";
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Initializing localized strings");
        trace.info("Initializing localized strings");
        const stringsMarker = InstrumentationService.perfMarkerStart("initializeStrings");
        await initializeStrings(context.app.locale);
        InstrumentationService.perfMarkerEnd(stringsMarker);
        trace.info("Complete initialization localized strings");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete initialization localized strings");

        // init DateTimeFormatter module with current locale data
        initializationStage = "initializeDateTimeFormatter";
        trace.info("Initializing datetimeformatter");
        // passing the context for locale info
        DateTimeFormatter.initialize(context.app.locale);
        trace.info("Complete initialization datetimeformatter");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete initialization datetimeformat");

        initializationStage = "setDefaultTimezone";
        trace.info("Setting default timezone");
        // Use the browser timezone as a best-guess for the user's timezone and initialize moment with it.
        // This will be overriden with the team timezone if it differs after team bootstrap. Initializing it here
        // is a perf optimization that ensures we do not launch the timeZoneChanged action unnecessarily after initializing
        // a team during cold boot, where we compare the current moment timezone to the team's timezone. Without setting some
        // timezone here, the current timezone will be undefined.
        moment.tz.setDefault(moment.tz.guess());
        trace.info("Complete setting default timezone");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete setting default timezone");

        initializationStage = "initializeIcons";
        trace.info("Initializing icons");
        // Register icons and pull the fonts from the default SharePoint cdn.
        initializeIcons();
        trace.info("Complete initializing icons");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete initializing icons");

        initializationStage = "registerIcons";
        trace.info("Registering icons");
        registerIcons({ icons: iconsData });
        trace.info("Complete registering icons");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete registering icons");

        // set our office fabric theme overrides
        initializationStage = "loadTheme";
        loadTheme({
            palette: {
                themeDark: styles.brandDark,
                themeDarkAlt: styles.brandDark,
                themePrimary: styles.brandDefault,
                themeSecondary: styles.brandDark
            },
            fonts: {},
            semanticColors: {}
        });
        trace.info("Complete loading theme");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete loading theme");

        initializationStage = "UserDataService.locateUserRegion";
        const locateUserMarker = InstrumentationService.perfMarkerStart("locateUserRegion");
        const locateUserResponse = await DataServices.UserDataService.locateUserRegion();
        InstrumentationService.perfMarkerEnd(locateUserMarker);
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete locateUserRegion");

        // End the logging session if the user navigates away
        initializationStage = "registerBeforeUnloadEvent";
        window.addEventListener("beforeunload", endLoggingSession);

        // Allow ctrl-F6 and shift-ctrl-F6 to route focus to the parent window and back to the tab.
        initializationStage = "registerKeyDownEvent";
        document.addEventListener("keydown", handleKeyDown);

        // Non-blocking login to Shifts service and initializing live updates
        initializationStage = "loginAndInitializeLiveUpdates";
        trace.info("Login and initialize live updates");
        loginAndInitializeLiveUpdates(
            getClientTypeFromHostContext(context),
            locateUserResponse.region?.afdServiceUrl
        );
        trace.info("Complete login and initialize live updates");
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete login and initialize live updates");

        // Initialize smart schedule polling before we notify tab load so polling can begin as soon as team/tag become available.
        SmartScheduleGenerationPollingService.initialize();

    // Tab load successful
        initializationStage = "notifyTabLoadSuccess";
        initializationCallback();
        logDetailedPerfEvent(InstrumentationService.events.AppPreInitStart, "Complete callback initialization");
    } catch (loginError) {
        // Tab load failed
        const errorMessage = loginError?.staffHubTopLevelErrorMessage ??
            loginError?.message ??
            "Failed to initialize the app";

        hostContainer.reportError(new Error(errorMessage));

        InstrumentationService.logPerfEvent(InstrumentationService.events.AppPreInitFailed, [
            getGenericEventPropertiesObject(InstrumentationService.properties.Error, errorMessage),
            getGenericEventPropertiesObject(InstrumentationService.properties.Stage, initializationStage)
        ]);

        // If there is an api error, it can get marked as handled in handleStaffHubServiceError which handles it as showing a global message
        // But since the app is in preInit stage, we should be handling the error here to show the blocking message.
        // This can be removed once we onboard to extension tab load error ui. But for now, we should force it to show the error message here
        if (loginError && loginError.isHandled) {
            loginError.isHandled = false;
        }
        // something went wrong during login, redirect to the access blocked page
        // which will check the error and show something appropriate
        handleBlockingBootstrapError(loginError, null);
    }
}

export const reInitializeLogging = (enabled: boolean) => {
     if (!enabled) {
           return;
        }
        setFeatureFlag("verbose", true);
        initializeLogging();
};

const initializeLogging = () => {
    const verboseLogging = isFeatureEnabled("verbose");
    // logging (info, warnings, errors)
    initializeTracer(verboseLogging);
    // shows errors within client UI (dev only)
    initializeErrorHandler(verboseLogging);
    // logs satchel actions and stiches to console
    // keeps record of satchel actions (useful for attaching to logs)
    // logs satchel action perf data to aria
    initializeSatchelMiddleware(verboseLogging);
};

export const logDetailedPerfEvent = (event: any, eventName: string) => {
    if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.DetailedLoggingEnabled)) {
        InstrumentationService.logPerfEvent(event, [
            getGenericEventPropertiesObject(InstrumentationService.properties.Trace, eventName)
        ]);
    }
};

const setUserInfoInState = (userId: string, tenantId: string): void => {
    setUser(new UserEntity(
        userId,
        "",
        userId,
        tenantId,
        "",
        "",
        "",
        "",
        "",
        false,
        false,
        "",
        "",
        ""
    ));
};

const loginAndInitializeLiveUpdates = async (clientType: ClientTypes, serviceUrl?: string): Promise<void> => {
    try {
        await loginToShiftsService(clientType);
        await initializeLiveUpdates(serviceUrl);
    } catch (exception) {
        if (exception?.isHandled) {
            exception.isHandled = false;
        }

        handleBlockingBootstrapError(exception, null);
    }
};

const loginToShiftsService = async (clientType: ClientTypes): Promise<void> => {
    const perfMarker = InstrumentationService.perfMarkerStart("loginToService");

    await DataServices.UserDataService.loginToService(clientType);

    InstrumentationService.perfMarkerEnd(perfMarker);
};

const initializeLiveUpdates = async (serviceUrl?: string): Promise<void> => {
    if (serviceUrl) {
        await SyncService.initialize();
    } else {
        trace.warn("Not starting Sync as service direct url not available");
    }
};

/**
 * Ends the logging session.
 */
function endLoggingSession(): void {
    InstrumentationService.endLogSession();
}

/**
 * Allows ctrl-F6 and shift-ctrl-F6 to route focus to the parent window.
 * @param e Keyboard event.
 */
function handleKeyDown(e: any): void {
    if (e.ctrlKey && e.key === 'F6') {
        e.preventDefault();
        window.parent.focus();
    } else if (e.ctrlKey && e.shiftKey && e.key === 'F6') {
        e.preventDefault();
        window.parent.focus();
    }
}

/**
 * Start the bootstrap process.
 * @param {string} groupId - the groupId of the team the user is logging in to
 * @param {string} teamId - the team the user is logging in to
 * @param {string} location - the routing location string
 * @param {H.History} history - browser history instance
 */
export async function initializeApplication(
    tenantId: string,
    groupId: string,
    teamId: string,
    location: string,
    history: H.History
): Promise<void> {
    const config: BootstrapConfig = {
        groupId,
        location,
        teamId,
        tenantId
    };

    const bootstrapMarker = InstrumentationService.perfMarkerStart("bootstrap");

    return bootstrap(config)
        .catch((httpError: StaffHubHttpError) => {
            // something went wrong during bootstrap, redirect to the access blocked page
            // which will check the error and show something appropriate
            handleBlockingBootstrapError(httpError, history);
        })
        .then(() => {
            InstrumentationService.perfMarkerEnd(bootstrapMarker);
        });
}

// TODO: Use platform agnostic client type
function setClientPlatformFromTeamsContext(context: HostContext) {
    const clientPlatform =
        context && context.host.clientType == HostClientType.desktop
            ? ClientPlatformTypes.TeamsDesktop
            : ClientPlatformTypes.TeamsWeb;
    NetworkService.setClientPlatform(clientPlatform);
    setHostPlatform(context.host.clientType);
}
